import { StripeElementsOptions, loadStripe } from '@stripe/stripe-js';

export const StripePromise = loadStripe(
    process.env.NEXT_PUBLIC_STRIPE_PUBLISH_KEY ?? ''
);

/*
 * Stripe Elements render inside an iframe, so they cannot take our classes and
 * they own their own DOM: field order, labels and layout are Stripe's. What we
 * do control is the Appearance API — `variables` for the palette and type, and
 * `rules` for per-element CSS from a fixed property list. That is enough to
 * make the fields match a DS field, which they did not.
 *
 * The old config asked for Inter from Google Fonts — the face the design
 * system dropped — so every Stripe field on the address, card and KYC forms
 * rendered in a typeface that appears nowhere else in the app.
 *
 * A custom font has to be reachable by URL from inside the iframe, and it has
 * to be absolute: a relative path resolves against Stripe's origin, not ours.
 * Ours are self-hosted at a stable path, so the origin is all that is needed —
 * and on the server there is no origin to read, which is why the options are
 * built by a function rather than frozen at import.
 *
 * Values are the DS tokens' own. They are literals because this config is
 * serialised across an iframe boundary, where `var(--ds-*)` would resolve
 * against Stripe's document and come back empty.
 */
const DS = {
    ink400: '#26251d',
    ink300: '#3f3f3c',
    surface000: '#ffffff',
    surface300: '#e6e5e1',
    surface600: '#262419',
    accentRed: '#e21414'
};

const fontSources = (): StripeElementsOptions['fonts'] => {
    if (typeof window === 'undefined') return [];
    const { origin } = window.location;
    return [
        { family: 'Helvetica Now Text', src: `url(${origin}/fonts/helvetica-now/HelveticaNowText-Regular.woff2)`, weight: '400' },
        { family: 'Helvetica Now Text', src: `url(${origin}/fonts/helvetica-now/HelveticaNowText-Medium.woff2)`, weight: '500' }
    ];
};

const buildOptions = (): StripeElementsOptions => ({
    fonts: fontSources(),
    appearance: {
        theme: 'stripe',
        variables: {
            fontFamily: '"Helvetica Now Text", "Helvetica Neue", Helvetica, Arial, sans-serif',
            // The DS field is 14px with a 12px label; Stripe's own scale steps
            // are flattened to those two so nothing lands off the ramp.
            fontSizeBase: '14px',
            fontSizeLg: '14px',
            fontSizeSm: '14px',
            fontSize2Xs: '12px',
            fontSize3Xs: '12px',
            spacingGridRow: '16px',
            borderRadius: '2px',
            colorBackground: DS.surface000,
            colorText: DS.ink400,
            colorTextSecondary: DS.ink300,
            colorTextPlaceholder: DS.ink300,
            colorDanger: DS.accentRed,
            colorPrimary: DS.surface600
        },
        rules: {
            // A DS field: 1px surface-300, square, 12px padding, 48 tall, and
            // no shadow anywhere in the system.
            '.Input': {
                border: `1px solid ${DS.surface300}`,
                borderRadius: '2px',
                boxShadow: 'none',
                padding: '12px',
                color: DS.ink400
            },
            '.Input:hover': { border: `1px solid ${DS.surface300}` },
            '.Input:focus': {
                border: `1px solid ${DS.surface600}`,
                boxShadow: 'none',
                outline: 'none'
            },
            '.Input--invalid': { border: `1px solid ${DS.accentRed}`, boxShadow: 'none' },
            '.Label': { color: DS.ink300, fontWeight: '400' },
            '.Error': { color: DS.accentRed }
        }
    }
});

/*
 * Still a plain value, because twenty call sites spread it — several reaching
 * into `.appearance.variables` — and they should not all have to become calls.
 *
 * Client components are server-rendered too, so this module is evaluated in
 * both places: on the server it yields no custom font, which costs nothing
 * because Stripe only mounts its iframe in the browser, where the origin is
 * readable and the fonts resolve.
 */
export const StripeOptions: StripeElementsOptions = buildOptions();
